import { Component, signal, OnInit, inject } from '@angular/core';
import { RouterOutlet, Router, RouterLink, RouterLinkActive, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MenuItem, MessageService } from 'primeng/api';
import { Toast } from 'primeng/toast';
import { AuthService, UserInfo } from './services/auth.service';
import { YearMonthFilterService } from './services/year-month-filter.service';
import { PageHeaderSearchActionService } from './services/page-header-search-action.service';
import { SessionExpiredService } from './services/session-expired.service';
import { Month } from './models/allocation.model';
import { filter } from 'rxjs/operators';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';

/** Path to page title for main header. Exported so the access matrix names pages the same way. */
export const ROUTE_TITLES: Record<string, string> = {
  '/dashboard/portfolio-overview': 'Portfolio Overview',
  '/dashboard/project-health-delivery': 'Project health & delivery',
  '/dashboard/invoicing-milestone-tracker': 'Invoicing & milestone tracker',
  '/allocations': 'Allocation Plans',
  '/forecast': 'Forecast',
  '/future-allocation': 'Future Allocation',
  '/outings': 'Team Outings',
  '/outings/summary': 'Outing Summary',
  '/outings/report': 'Team Outing Report',
  '/outing-rsvp': 'Outing Invitation',
  '/invoicing-plan': 'Invoicing Plan',
  '/invoice-planning-grid': 'Invoicing Summary View',
  '/employees': 'Employee Management',
  '/projects': 'Project List',
  '/projects-inventory': 'Project inventory',
  '/cost-centers': 'Cost Centers',
  '/roles': 'Role Management',
  '/designations': 'Designation Management',
  '/categories': 'Category Management',
  '/service-groups': 'Service Group Management',
  '/clients': 'Client Management',
  '/availability-report': 'Availability Report',
  '/timesheet-report': 'Work Log Report',
  '/effort-variance-report': 'Effort Variance Report',
  '/project-utilization-report': 'Projects Utilization Report',
  '/timesheet-drafts': 'Draft Work Logs',
  '/employee-projects-history': 'Employee Projects History',
  '/timesheet': 'Work Log',
  '/my-project-tasks': 'Project Task Tracker',
  '/tasks': 'Task Management',
  '/checklist-management': 'Process Check List',
  '/companies': 'Company Management',
  '/counties': 'Country Management',
  '/contracts-maintenance': 'Contracts maintenance',
  '/contracts-summary-view': 'Contracts Summary View',
  '/onboarding': 'Staff Onboarding',
  '/onboarding/employees': 'Onboarding Employees',
  '/onboarding/email-templates': 'Email Templates',
  '/onboarding/task-templates': 'Onboarding Templates',
  '/offboarding': 'Staff Offboarding',
  '/offboarding/collection': 'Offboarding Records',
  '/offboarding/task-templates': 'Offboarding Templates',
  '/presales/summary': 'Presales Summary',
  '/presales/inventory': 'Presales Project Inventory',
  '/presales/activity-tracker': 'PreSale Activity Tracker',
  '/presales/consolidated-board': 'Consolidated Board',
  '/presales/details': 'Presales Project Details',
  '/presales/clients': 'Presales Clients',
  '/presales/cost-centers': 'Presales Cost Centers',
  '/audit-logs': 'Audit Logs',
  '/access-matrix': 'Access Matrix',
  '/notification-health': 'Notification Health',
  '/notification-settings': 'Notification Settings'
};

@Component({
  selector: 'app-root',
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive, Button, InputText, Toast],
  providers: [MessageService],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit {
  private router = inject(Router);
  protected readonly title = signal('team-allocation-web');
  user: UserInfo | null = null;
  isAuthenticated = false;

  menuItems: MenuItem[] = [];
  /** Current page title for main header (derived from route). */
  pageTitle = signal<string>('');
  /** Sidebar nav groups: expanded state by group label. */
  expandedGroups: Record<string, boolean> = {};
  /** Theme: false = light, true = dark (placeholder for future). */
  darkMode = signal(false);
  /** Sidebar collapsed: only icons visible, nav scrolls. */
  sidebarCollapsed = signal(false);
  /** True when current route is allocation page (show year/month in header). */
  isOnAllocationPage = signal(false);
  /** True when current route is forecast page (show year in header). */
  isOnForecastPage = signal(false);
  /** True when current route is future allocation page (show year in header). */
  isOnFutureAllocationPage = signal(false);
  /** True when current route is timesheet page (show year/month in header). */
  isOnTimesheetPage = signal(false);
  /**
   * True on the Project Task Tracker, which shares the Work Log year/month header controls —
   * allocation is per month, so the period picks which projects are listed.
   */
  isOnMyProjectTasksPage = signal(false);
  /** True when current route is project management (projects-inventory) page. */
  isOnProjectsPage = signal(false);
  /** True when current route is project list (projects) page. */
  isOnProjectsListPage = signal(false);
  /** True when current route is employee management page. */
  isOnEmployeesPage = signal(false);
  /** True when current route is category management page. */
  isOnCategoriesPage = signal(false);
  /** True when current route is client management page. */
  isOnClientsPage = signal(false);
  /** True when current route is cost centers page. */
  isOnCostCentersPage = signal(false);
  /** True when current route is country (counties) management page. */
  isOnCountiesPage = signal(false);
  /** True when current route is company management page. */
  isOnCompaniesPage = signal(false);
  /** True when current route is task management page. */
  isOnTasksPage = signal(false);
  /** True when current route is role management page. */
  isOnRolesPage = signal(false);
  /** True when current route is designation management page. */
  isOnDesignationsPage = signal(false);
  /** True when current route is service group management page. */
  isOnServiceGroupsPage = signal(false);
  /** True when current route is invoicing plan page. */
  isOnInvoicingPlanPage = signal(false);
  /** True when current route is invoice planning grid page. */
  isOnInvoicePlanningGridPage = signal(false);
  /** True when current route is contracts maintenance page. */
  isOnContractsMaintenancePage = signal(false);
  /** True when current route is contracts summary view page. */
  isOnContractsSummaryViewPage = signal(false);
  /** True when current route is availability report page. */
  isOnAvailabilityReportPage = signal(false);
  /** True when current route is effort variance report page. */
  isOnEffortVarianceReportPage = signal(false);
  isOnProjectUtilizationReportPage = signal(false);
  /** True when current route is employee projects history page. */
  isOnEmployeeProjectsHistoryPage = signal(false);
  /** True when current route is timesheet drafts page. */
  isOnTimesheetDraftsPage = signal(false);
  /** True when current route is timesheet report page. */
  isOnTimesheetReportPage = signal(false);
  /** True when on onboarding sub-pages (employees list) — NOT the portal. */
  isOnOnboardingSubPage = signal(false);
  /** True when on offboarding sub-pages (collection) — NOT the portal. */
  isOnOffboardingSubPage = signal(false);
  /** True when on any Presales sub-page (inventory / details / clients). */
  isOnPresalesPage = signal(false);
  isOnAccessMatrixPage = signal(false);
  /** True when on the Team Outings page. */
  isOnOutingsPage = signal(false);
  isOnOutingReportPage = signal(false);
  /** Year/month filter (synced from YearMonthFilterService for header dropdowns). */
  allocationYear = signal(0);
  allocationMonth = signal(1);
  /** Header search text (synced from PageHeaderSearchActionService for Project/Employee Management etc.). */
  headerSearchText = signal('');

  /** User name only (no "BISTEC Global" or company suffix). */
  get displayNameOnly(): string {
    const name = this.user?.displayName ?? '';
    return name.split('|')[0].trim();
  }

  /** Initials for sidebar avatar (e.g. "John Doe" -> "JD"). */
  getInitials(): string {
    const n = this.displayNameOnly;
    if (!n) return '?';
    const parts = n.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return n.slice(0, 2).toUpperCase();
  }

  /** Resolve page title from current URL and re-sync header year/month when on allocation or forecast. */
  private setPageTitleFromRoute(): void {
    const url = this.router.url.split('?')[0];
    // Public token routes (e.g. /outing-rsvp/{token}) — match by prefix, not exact URL.
    const title = url.startsWith('/outing-rsvp')
      ? 'Outing Invitation'
      : (ROUTE_TITLES[url] ?? this.formatPathAsTitle(url));
    this.pageTitle.set(title);
    this.isOnAllocationPage.set(url.startsWith('/allocations'));
    this.isOnForecastPage.set(url.startsWith('/forecast'));
    this.isOnFutureAllocationPage.set(url.startsWith('/future-allocation'));
    this.isOnTimesheetPage.set(url === '/timesheet' || url.startsWith('/timesheet/'));
    this.isOnMyProjectTasksPage.set(url.startsWith('/my-project-tasks'));
    this.isOnTimesheetDraftsPage.set(url.startsWith('/timesheet-drafts'));
    this.isOnTimesheetReportPage.set(url.startsWith('/timesheet-report'));
    this.isOnProjectsPage.set(url.startsWith('/projects-inventory'));
    this.isOnProjectsListPage.set(url === '/projects' || url.startsWith('/projects/'));
    this.isOnEmployeesPage.set(url.startsWith('/employees'));
    this.isOnCategoriesPage.set(url.startsWith('/categories'));
    this.isOnClientsPage.set(url.startsWith('/clients'));
    this.isOnCostCentersPage.set(url.startsWith('/cost-centers'));
    this.isOnCountiesPage.set(url.startsWith('/counties'));
    this.isOnCompaniesPage.set(url.startsWith('/companies'));
    this.isOnTasksPage.set(url.startsWith('/tasks'));
    this.isOnRolesPage.set(url.startsWith('/roles'));
    this.isOnDesignationsPage.set(url.startsWith('/designations'));
    this.isOnServiceGroupsPage.set(url.startsWith('/service-groups'));
    this.isOnInvoicingPlanPage.set(url.startsWith('/invoicing-plan'));
    this.isOnInvoicePlanningGridPage.set(url.startsWith('/invoice-planning-grid'));
    this.isOnContractsMaintenancePage.set(url.startsWith('/contracts-maintenance'));
    this.isOnContractsSummaryViewPage.set(url.startsWith('/contracts-summary-view'));
    this.isOnAvailabilityReportPage.set(url.startsWith('/availability-report'));
    this.isOnEffortVarianceReportPage.set(url.startsWith('/effort-variance-report'));
    this.isOnProjectUtilizationReportPage.set(url.startsWith('/project-utilization-report'));
    this.isOnEmployeeProjectsHistoryPage.set(url.startsWith('/employee-projects-history'));
    this.isOnOnboardingSubPage.set(url.startsWith('/onboarding/'));
    this.isOnOffboardingSubPage.set(url.startsWith('/offboarding/'));
    this.isOnPresalesPage.set(url.startsWith('/presales/'));
    this.isOnAccessMatrixPage.set(url.startsWith('/access-matrix'));
    // Manage Outings only (NOT /outings/summary, which has its own in-page name search).
    this.isOnOutingsPage.set(url === '/outings');
    // Outing Report drives its employee search from the main header.
    this.isOnOutingReportPage.set(url.startsWith('/outings/report'));
    // Re-sync header filter from service so year/month match after refresh or route change
    if (url.startsWith('/allocations') || url.startsWith('/forecast') || url.startsWith('/timesheet') || url.startsWith('/my-project-tasks') || url.startsWith('/invoice-planning-grid') || url.startsWith('/contracts-summary-view') || url.startsWith('/availability-report')) {
      this.allocationYear.set(this.yearMonthFilterService.currentYear);
      this.allocationMonth.set(this.yearMonthFilterService.currentMonth);
    }
  }

  private formatPathAsTitle(path: string): string {
    if (!path || path === '/') return 'Home';
    const segment = path.replace(/^\//, '').split('/')[0];
    return segment.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  /** Toggle sidebar nav group expand/collapse. */
  toggleNavGroup(groupLabel: string): void {
    this.expandedGroups[groupLabel] = !this.expandedGroups[groupLabel];
  }

  /** Whether a nav group is expanded. Default: only the group containing the current route. */
  isNavGroupExpanded(groupLabel: string): boolean {
    return this.expandedGroups[groupLabel] === true;
  }

  /** Update expanded groups so only the group containing the current route is expanded. */
  private setExpandedGroupFromRoute(): void {
    const rawPath = this.router.url.split('?')[0];
    const path = rawPath.replace(/\/$/, '') || '/';
    const next: Record<string, boolean> = {};
    for (const group of this.menuItems) {
      const label = group.label ?? '';
      const hasCurrentRoute =
        group.items?.some(
          (item) => {
            const link = typeof item?.routerLink === 'string' ? item.routerLink.replace(/\/$/, '') || '/' : '';
            return link && (path === link || path.startsWith(link + '/'));
          }
        ) ?? false;
      next[label] = hasCurrentRoute;
    }
    this.expandedGroups = next;
  }

  /** Toggle dark/light theme and persist preference. */
  toggleTheme(): void {
    this.darkMode.update(v => !v);
    const isDark = this.darkMode();
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    localStorage.setItem('apex-theme', isDark ? 'dark' : 'light');
  }

  /** Toggle sidebar collapsed (icons only) / expanded. */
  toggleSidebar(): void {
    this.sidebarCollapsed.update(v => !v);
  }

  /** Allocation filter: year/month options (for header dropdowns). */
  get allocationYearOptions() {
    return this.yearMonthFilterService.getYearOptions();
  }
  get allocationMonthOptions() {
    return this.yearMonthFilterService.getMonthOptions();
  }

  onAllocationYearChange(value: number): void {
    this.yearMonthFilterService.setYear(value);
  }
  onAllocationMonthChange(value: number): void {
    this.yearMonthFilterService.setMonth(value as Month);
  }

  onHeaderSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement)?.value ?? '';
    this.headerSearchText.set(value);
    this.pageHeaderService.setSearchText(value);
  }
  onHeaderSearchClear(): void {
    this.headerSearchText.set('');
    this.pageHeaderService.setSearchText('');
  }

  constructor(
    private authService: AuthService,
    private yearMonthFilterService: YearMonthFilterService,
    protected pageHeaderService: PageHeaderSearchActionService,
    protected sessionExpired: SessionExpiredService
  ) {
    // Sync header filter from service immediately so first paint (and after refresh) shows correct year/month
    this.allocationYear.set(this.yearMonthFilterService.currentYear);
    this.allocationMonth.set(this.yearMonthFilterService.currentMonth);
  }

  ngOnInit(): void {
    // Restore saved theme preference so sidebar and dropdowns match
    const savedTheme = localStorage.getItem('apex-theme');
    if (savedTheme === 'dark') {
      this.darkMode.set(true);
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      this.darkMode.set(false);
      document.documentElement.setAttribute('data-theme', 'light');
    }
    this.setPageTitleFromRoute();
    this.yearMonthFilterService.year.subscribe(y => this.allocationYear.set(y));
    this.yearMonthFilterService.month.subscribe(m => this.allocationMonth.set(m));

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        this.setPageTitleFromRoute();
        this.setExpandedGroupFromRoute();
      });

    this.pageHeaderService.searchText.subscribe(v => this.headerSearchText.set(v ?? ''));

    this.authService.user$.subscribe(user => {
      this.user = user;
      this.buildMenuItems();
      this.setExpandedGroupFromRoute();
    });

    this.authService.isAuthenticated$.subscribe(isAuth => {
      this.isAuthenticated = isAuth;
      // Don't redirect if we're on login page or unauthorized page
      // Also don't redirect during initial load - let MSAL guard handle it
      const currentUrl = this.router.url;
      if (!isAuth && 
          currentUrl !== '/login' && 
          currentUrl !== '/unauthorized' &&
          !currentUrl.includes('#') && // Don't redirect during MSAL redirect processing
          !currentUrl.includes('code=') && // Don't redirect during OAuth code exchange
          !currentUrl.includes('error=')) { // Don't redirect during error handling
        // Small delay to allow MSAL to process redirect response
        setTimeout(() => {
          if (!this.authService.isAuthenticated() && this.router.url === currentUrl) {
            this.router.navigate(['/login']);
          }
        }, 100);
      }
    });
  }

  private buildMenuItems(): void {
    if (!this.user) {
      this.menuItems = [];
      return;
    }

    const hasProjectDetailsAccess = this.user.isAdmin || this.user.isPM;
    const hasProjectsManagementAccess = this.user.isAdmin || this.user.isPM;

    // Build Projects Management submenu items
    const projectsManagementItems: MenuItem[] = [];
    
    // Project inventory - Admin, PM (permission matrix)
    if (this.user.isAdmin || this.user.isPM) {
      projectsManagementItems.push({
        label: 'Project inventory',
        icon: 'pi pi-list',
        routerLink: '/projects-inventory'
      });
    }
    
    // Project details - Admin, PM only (permission matrix)
    if (hasProjectDetailsAccess) {
      projectsManagementItems.push({
        label: 'Project Details',
        icon: 'pi pi-info-circle',
        routerLink: '/projects'
      });
    }

    // Settings: Admin sees all items; P&C sees only Employees (permission matrix)
    const settingsItems: MenuItem[] = [];
    if (this.user.isAdmin) {
      settingsItems.push(
        { label: 'Employee', icon: 'pi pi-users', routerLink: '/employees' },
        { label: 'Cost Centers', icon: 'pi pi-dollar', routerLink: '/cost-centers' },
        { label: 'Role', icon: 'pi pi-id-card', routerLink: '/roles' },
        { label: 'Designation', icon: 'pi pi-briefcase', routerLink: '/designations' },
        { label: 'Category', icon: 'pi pi-tags', routerLink: '/categories' },
        { label: 'Service Group', icon: 'pi pi-sitemap', routerLink: '/service-groups' },
        { label: 'Client', icon: 'pi pi-building', routerLink: '/clients' },
        { label: 'Country', icon: 'pi pi-map', routerLink: '/counties' },
        { label: 'Company', icon: 'pi pi-building', routerLink: '/companies' },
        { label: 'Tasks', icon: 'pi pi-list', routerLink: '/tasks' },
        { label: 'Process Check List', icon: 'pi pi-list-check', routerLink: '/checklist-management' }
      );
    } else if (this.user.isPc) {
      // P&C manages the employee lifecycle + the HR taxonomy that feeds the Add-Employee form.
      // Organisational data (Cost Centers, Service Groups, Clients, Countries, Companies, Tasks,
      // Process Checklists) stays Admin-only.
      settingsItems.push(
        { label: 'Employee', icon: 'pi pi-users', routerLink: '/employees' },
        { label: 'Role', icon: 'pi pi-id-card', routerLink: '/roles' },
        { label: 'Designation', icon: 'pi pi-briefcase', routerLink: '/designations' },
        { label: 'Category', icon: 'pi pi-tags', routerLink: '/categories' }
      );
    }

    this.menuItems = [
      {
        label: 'Dashboard',
        icon: 'pi pi-th-large',
        visible: this.user.isAdmin || this.user.isPM || this.user.isFinance || this.user.isSales,
        items: [
          {
            label: 'Portfolio Overview',
            icon: 'pi pi-chart-bar',
            routerLink: '/dashboard/portfolio-overview'
          },
          {
            label: 'Project health & delivery',
            icon: 'pi pi-heart',
            routerLink: '/dashboard/project-health-delivery'
          },
          {
            label: 'Invoicing & milestone tracker',
            icon: 'pi pi-file-check',
            routerLink: '/dashboard/invoicing-milestone-tracker'
          }
        ]
      },
      {
        label: 'Projects Management',
        icon: 'pi pi-folder',
        visible: hasProjectsManagementAccess,
        items: projectsManagementItems
      },
      // ================= Presales Project Management =================
      // Access: Admin + PM (matches PresalesAccess policy in the backend).
      // Placed directly under Projects Management so pipeline work stays adjacent
      // to delivery work in the nav.
      {
        label: 'Presales',
        icon: 'pi pi-briefcase',
        visible: this.user.isAdmin || this.user.isPM || this.user.isSales,
        items: [
          {
            label: 'Presales Summary',
            icon: 'pi pi-chart-pie',
            routerLink: '/presales/summary'
          },
          {
            label: 'Presales Project Inventory',
            icon: 'pi pi-list',
            routerLink: '/presales/inventory'
          },
          {
            label: 'PreSale Activity Tracker',
            icon: 'pi pi-th-large',
            routerLink: '/presales/activity-tracker'
          },
          {
            label: 'Consolidated Board',
            icon: 'pi pi-objects-column',
            routerLink: '/presales/consolidated-board'
          },
          {
            label: 'Presales Project Details',
            icon: 'pi pi-table',
            routerLink: '/presales/details'
          },
          {
            label: 'Presales Clients',
            icon: 'pi pi-users',
            routerLink: '/presales/clients'
          },
          {
            label: 'Presales Cost Centers',
            icon: 'pi pi-wallet',
            routerLink: '/presales/cost-centers'
          }
        ]
      },
      {
        label: 'Settings',
        icon: 'pi pi-cog',
        visible: this.user.isAdmin || this.user.isPc,
        items: settingsItems
      },
      {
        label: 'Allocation Plans',
        icon: 'pi pi-calendar-plus',
        visible: this.user.isAdmin || this.user.isPM || this.user.isFinance || this.user.isSales,
        items: [
          {
            label: 'Monthly Allocation',
            icon: 'pi pi-calendar',
            routerLink: '/allocations'
          },
          {
            label: 'Forecast',
            icon: 'pi pi-chart-line',
            routerLink: '/forecast'
          },
          {
            label: 'Future Allocation',
            icon: 'pi pi-calendar-clock',
            routerLink: '/future-allocation'
          }
        ]
      },
      {
        label: 'Team Outings',
        icon: 'pi pi-users',
        visible: this.user.isAdmin || this.user.isPM || this.user.isMarketing,
        items: [
          {
            label: 'Manage Outings',
            icon: 'pi pi-calendar-plus',
            routerLink: '/outings',
            visible: this.user.isAdmin || this.user.isPM
          },
          {
            label: 'Outing Summary',
            icon: 'pi pi-chart-bar',
            routerLink: '/outings/summary',
            visible: this.user.isAdmin || this.user.isPM || this.user.isMarketing
          },
          {
            label: 'Outing Report',
            icon: 'pi pi-chart-line',
            routerLink: '/outings/report',
            visible: this.user.isAdmin || this.user.isPM || this.user.isMarketing
          }
        ]
      },
      {
        label: 'Work Log',
        icon: 'pi pi-clock',
        visible: this.user.isAdmin || this.user.isPM || this.user.isFinance || this.user.isSoftware || this.user.isAccounting || this.user.isMarketing || this.user.isSales || this.user.isOperations || this.user.isSupport,
        items: [
          {
            label: 'Enter Work Log',
            icon: 'pi pi-pencil',
            routerLink: '/timesheet'
          },
          {
            label: 'Draft Work Logs',
            icon: 'pi pi-file-edit',
            routerLink: '/timesheet-drafts',
            visible: this.user.isAdmin || this.user.isPM || this.user.isSales || this.user.isOperations || this.user.isSupport
          }
        ]
      },
      {
        label: 'Finance',
        icon: 'pi pi-money-bill',
        visible: this.user.isAdmin || this.user.isPM || this.user.isFinance || this.user.isSales,
        items: [
          {
            label: 'Invoicing Plan',
            icon: 'pi pi-file',
            routerLink: '/invoicing-plan',
            visible: this.user.isAdmin || this.user.isPM || this.user.isFinance || this.user.isSales
          },
          {
            label: 'Invoicing Summary View',
            icon: 'pi pi-table',
            routerLink: '/invoice-planning-grid',
            visible: this.user.isAdmin || this.user.isPM || this.user.isFinance || this.user.isSales
          },
          {
            label: 'Contracts maintenance',
            icon: 'pi pi-folder-open',
            routerLink: '/contracts-maintenance',
            visible: this.user.isAdmin || this.user.isFinance
          },
          {
            label: 'Contracts Summary View',
            icon: 'pi pi-calendar-times',
            routerLink: '/contracts-summary-view',
            visible: this.user.isAdmin || this.user.isFinance
          }
        ]
      },
      {
        label: 'Report',
        icon: 'pi pi-chart-bar',
        visible: this.user.isAdmin || this.user.isPM || this.user.isFinance || this.user.isSales,
        items: [
          {
            label: 'Work Log Report',
            icon: 'pi pi-clock',
            routerLink: '/timesheet-report'
          },
          {
            label: 'Availability Report',
            icon: 'pi pi-users',
            routerLink: '/availability-report'
          },
          {
            // Allocation vs actual effort — delivery and costing, so not shown to Sales.
            label: 'Effort Variance Report',
            icon: 'pi pi-chart-line',
            routerLink: '/effort-variance-report',
            visible: this.user.isAdmin || this.user.isPM || this.user.isFinance
          },
          {
            // Same plan-vs-actual data as above, grouped by project — same audience.
            label: 'Projects Utilization Report',
            icon: 'pi pi-chart-bar',
            routerLink: '/project-utilization-report',
            visible: this.user.isAdmin || this.user.isPM || this.user.isFinance
          },
          {
            label: 'Employee Projects History',
            icon: 'pi pi-history',
            routerLink: '/employee-projects-history'
          }
        ]
      },
      {
        label: 'Onboarding',
        icon: 'pi pi-user-plus',
        visible: this.user.isAdmin || this.user.isPc || this.user.isIt || this.user.isLnD,
        items: [
          {
            label: 'Onboarding Portal',
            icon: 'pi pi-home',
            routerLink: '/onboarding'
          },
          {
            label: 'Employees',
            icon: 'pi pi-users',
            routerLink: '/onboarding/employees'
          },
          {
            label: 'Email Templates',
            icon: 'pi pi-envelope',
            routerLink: '/onboarding/email-templates',
            visible: this.user.isAdmin || this.user.isPc
          },
          {
            label: 'Onboarding Templates',
            icon: 'pi pi-list-check',
            routerLink: '/onboarding/task-templates',
            visible: this.user.isAdmin || this.user.isPc
          },
        ]
      },
      {
        label: 'Offboarding',
        icon: 'pi pi-user-minus',
        visible: this.user.isAdmin || this.user.isPc || this.user.isIt || this.user.isLnD,
        items: [
          {
            label: 'Offboarding Portal',
            icon: 'pi pi-home',
            routerLink: '/offboarding'
          },
          {
            label: 'Record Collection',
            icon: 'pi pi-list',
            routerLink: '/offboarding/collection'
          },
          {
            label: 'Offboarding Templates',
            icon: 'pi pi-list-check',
            routerLink: '/offboarding/task-templates',
            visible: this.user.isAdmin || this.user.isPc
          }
        ]
      },
      {
        label: 'Administration',
        icon: 'pi pi-shield',
        visible: this.user.isAdmin,
        items: [
          {
            label: 'Access Matrix',
            icon: 'pi pi-table',
            routerLink: '/access-matrix'
          },
          {
            label: 'Audit Logs',
            icon: 'pi pi-history',
            routerLink: '/audit-logs'
          },
          {
            label: 'Notification Health',
            icon: 'pi pi-heart',
            routerLink: '/notification-health'
          },
          {
            label: 'Notification Settings',
            icon: 'pi pi-envelope',
            routerLink: '/notification-settings'
          }
        ]
      },
    ];
  }

  /** Called from the session-expired modal's "Sign in again" button: dismiss modal, then go to login. */
  reloginAfterSessionExpiry(): void {
    this.sessionExpired.reset();
    this.router.navigate(['/login']);
  }

  private isLoggingOut = false;

  logout(): void {
    // Prevent multiple clicks
    if (this.isLoggingOut) {
      return;
    }
    this.isLoggingOut = true;

    // Clear all authentication state and cache (local logout, no MSAL redirect)
    this.authService.logout();
    
    // Navigate directly to login page
    this.router.navigate(['/login']).then(() => {
      // Reset flag after navigation
      setTimeout(() => {
        this.isLoggingOut = false;
      }, 500);
    });
  }
}
